import type { StylesConfigDto } from '@builder4/shared/dto/styles';
import type {
  PageProfileOutline,
  SectionOutlineEntry,
  GeneratedSection,
} from '../types/page-profile';
import type { ModelOption, AiGenerationStats } from '../types';
import { nonStreamingCompletion } from '../api/anthropic-client';
import { buildSectionGenerationPrompt } from '../prompts/page-profile-section-prompt';

/**
 * Callbacks for section generation progress
 */
export type SectionGenerationCallbacks = {
  /** Called when a complete section XML is ready */
  onSectionComplete: (section: GeneratedSection) => void;
  /** Called on error */
  onError: (error: Error) => void;
};

/**
 * Stage 3: GENERATE SECTIONS (Page Profile Mode)
 *
 * For each section in the outline, generates complete layout + content XML.
 * Non-streaming, sequential execution with context passing between sections.
 */
export const generateSectionsFromOutline = async (params: {
  outline: PageProfileOutline;
  styles: StylesConfigDto;
  callbacks: SectionGenerationCallbacks;
  signal?: AbortSignal;
  model?: ModelOption;
  stats?: AiGenerationStats;
}): Promise<GeneratedSection[]> => {
  const { outline, styles, callbacks, signal, model, stats } = params;
  const generatedSections: GeneratedSection[] = [];

  for (let i = 0; i < outline.sections.length; i++) {
    if (signal?.aborted) {
      return generatedSections;
    }

    const sectionEntry = outline.sections[i];

    try {
      const section = await generateSingleSection({
        outline,
        sectionEntry,
        sectionIndex: i,
        totalSections: outline.sections.length,
        styles,
        signal,
        model,
        stats,
      });

      if (section) {
        generatedSections.push(section);
        callbacks.onSectionComplete(section);
      }
    } catch (error) {
      console.error(`[PageProfile] Section ${i} generation failed:`, error);
      callbacks.onError(error instanceof Error ? error : new Error(String(error)));
      // Continue with remaining sections rather than aborting entirely
    }
  }

  if (stats?.trackStage && stats.sectionDurations.length > 0) {
    stats.trackStage('sections', {
      model: model ?? 'sonnet-4.6',
      inputTokens: stats.sectionInputTokens,
      outputTokens: stats.sectionOutputTokens,
      durationMs: stats.sectionDurations.reduce((a, b) => a + b, 0),
      sectionCount: stats.sectionDurations.length,
      longestSectionDurationMs: Math.max(...stats.sectionDurations),
    });
  }

  return generatedSections;
};

/**
 * Generate a single section with combined layout + content (non-streaming)
 */
const generateSingleSection = async (params: {
  outline: PageProfileOutline;
  sectionEntry: SectionOutlineEntry;
  sectionIndex: number;
  totalSections: number;
  styles: StylesConfigDto;
  signal?: AbortSignal;
  model?: ModelOption;
  stats?: AiGenerationStats;
}): Promise<GeneratedSection | null> => {
  const { outline, sectionEntry, sectionIndex, totalSections, styles, signal, model, stats } =
    params;

  const { system, user } = buildSectionGenerationPrompt({
    outline,
    sectionEntry,
    sectionIndex,
    totalSections,
    styles,
  });

  console.log(`[PageProfile Section ${sectionIndex}] Generating ${sectionEntry.type}...`);
  console.log(`[PageProfile Section ${sectionIndex}] System prompt:`, system);
  console.log(`[PageProfile Section ${sectionIndex}] User prompt:`, user);

  const t0 = Date.now();
  const result = await nonStreamingCompletion({
    model: model ?? 'sonnet-4.6',
    maxTokens: 8000,
    systemPrompt: system,
    userPrompt: user,
    signal,
  });
  if (stats) {
    stats.sectionDurations.push(Date.now() - t0);
    stats.totalInputTokens += result.inputTokens;
    stats.totalOutputTokens += result.outputTokens;
    stats.sectionInputTokens += result.inputTokens;
    stats.sectionOutputTokens += result.outputTokens;
  }

  const cleanedXml = cleanSectionXml(result.text);

  // Validate the XML starts with <section>
  if (!cleanedXml.startsWith('<section')) {
    console.warn(
      `[PageProfile] Section ${sectionIndex} did not start with <section>, got: ${cleanedXml.slice(0, 50)}...`,
    );
    // Try to extract section from response anyway
    const sectionMatch = cleanedXml.match(/<section[\s\S]*<\/section>/);
    if (sectionMatch) {
      return {
        xml: sectionMatch[0],
        index: sectionIndex,
        summary: extractSummary(sectionMatch[0], sectionEntry),
        layoutPattern: detectLayoutPattern(sectionMatch[0]),
      };
    }
    return null;
  }

  return {
    xml: cleanedXml,
    index: sectionIndex,
    summary: extractSummary(cleanedXml, sectionEntry),
    layoutPattern: detectLayoutPattern(cleanedXml),
  };
};

/**
 * Clean up section XML from the model response.
 * Strips markdown fencing if present and trims whitespace.
 */
const cleanSectionXml = (xml: string): string => {
  let cleaned = xml.trim();

  // Strip markdown code fences
  const codeBlockMatch = cleaned.match(/```(?:xml)?\s*([\s\S]*?)```/);
  if (codeBlockMatch) {
    cleaned = codeBlockMatch[1].trim();
  }

  // Remove any leading text before <section
  const sectionStart = cleaned.indexOf('<section');
  if (sectionStart > 0) {
    cleaned = cleaned.slice(sectionStart);
  }

  // Remove any trailing text after </section>
  const sectionEnd = cleaned.lastIndexOf('</section>');
  if (sectionEnd !== -1) {
    cleaned = cleaned.slice(0, sectionEnd + '</section>'.length);
  }

  return cleaned;
};

/**
 * Extract a brief summary from the generated section.
 * Uses the section type + first heading text.
 */
const extractSummary = (xml: string, entry: SectionOutlineEntry): string => {
  // Try to find the first paragraph text (likely the headline)
  const paragraphMatch = xml.match(/<paragraph[^>]*>([^<]+)<\/paragraph>/);
  const headingText = paragraphMatch ? paragraphMatch[1].trim() : '';

  if (headingText && headingText.length < 100) {
    return `${entry.type}: "${headingText}"`;
  }

  return `${entry.type}: ${entry.purpose.slice(0, 60)}`;
};

/**
 * Detect the layout pattern used in the generated section.
 * Simple heuristic based on column count and structure.
 */
const detectLayoutPattern = (xml: string): string => {
  const columnMatches = xml.match(/<column/g) || [];
  const columnCount = columnMatches.length;
  const hasRow = xml.includes('<row');
  const hasDirection = xml.includes('direction="row"');

  if (columnCount === 0) {
    return 'single-column';
  }

  if (columnCount === 2 && hasDirection) {
    // Check if it's left or right heavy
    const firstColumnMatch = xml.match(/<column[^>]*width="(\d+)%"/);
    if (firstColumnMatch) {
      const width = parseInt(firstColumnMatch[1], 10);
      if (width >= 55) return 'two-column-left';
      if (width <= 45) return 'two-column-right';
    }
    return 'split-screen';
  }

  if (columnCount === 3) {
    return 'three-column';
  }

  if (columnCount === 4) {
    return 'four-column';
  }

  if (columnCount > 4) {
    if (hasRow) {
      return 'grid';
    }
    return 'multi-column';
  }

  return 'single-column';
};
